import { useMutation } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  inviteExistingUserToProject,
  type Project,
  projectQueries,
} from '@/lib/queries/project';
import { generateMutationKey } from '@/lib/queries/utils';

interface Props {
  mainProject: Project;
  email: string;
  closeDialog: () => void;
}

export const InviteExistingUserDialog = ({
  mainProject,
  email,
  closeDialog,
}: Props): JSX.Element => {
  const { toast } = useToast();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: generateMutationKey(
      projectQueries.detail(mainProject.id),
      'invite-existing-user',
      true,
    ),
    mutationFn: async () =>
      inviteExistingUserToProject(mainProject.id, { email }),
    onSuccess: () => {
      toast({
        title:
          'configuration.projectManagement.inviteExistingUserDialog.button.success',
        variant: 'default',
      });
      closeDialog();
    },
    onError: () => {
      toast({
        title:
          'configuration.projectManagement.inviteExistingUserDialog.button.error',
        variant: 'destructive',
      });
    },
  });

  return (
    <>
      <DialogDescription>
        <FormattedMessage id="configuration.projectManagement.inviteExistingUserDialog.subtitle" />
        <br />
        <i>
          <FormattedMessage id="configuration.projectManagement.inviteExistingUserDialog.instructions" />
        </i>
      </DialogDescription>
      <DialogFooter>
        <Button
          variant="main"
          disabled={isPending}
          onClick={() => mutateAsync()}
        >
          {isPending ? (
            <FormattedMessage id="configuration.projectManagement.inviteExistingUserDialog.button.pending" />
          ) : (
            <FormattedMessage id="configuration.projectManagement.inviteExistingUserDialog.button.label" />
          )}
        </Button>
      </DialogFooter>
    </>
  );
};
